import { useReducer } from 'react';

import { type IAction } from 'src/utils/ts-utilities';

import { type IMemberInvite } from './types';

enum ActionEnum {
  SET_INITIAL = 'set-initial',
  CREATE_INVITE = 'create-invite',
  ADD_MEMBER = 'add-member',
  REMOVE_MEMBER = 'remove-member',
  CANCEL_ADD_MEMBER = 'cancel-add-member',
}

type IActionReducer =
  | IAction<ActionEnum.CREATE_INVITE>
  | IAction<ActionEnum.ADD_MEMBER, IMemberInvite>
  | IAction<ActionEnum.REMOVE_MEMBER, string>
  | IAction<ActionEnum.CANCEL_ADD_MEMBER>
  | IAction<ActionEnum.SET_INITIAL>;

export type IState = {
  step: 'initial' | 'add-members' | 'invite-members';
  members: IMemberInvite[];
};

const initialState: IState = {
  step: 'initial',
  members: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.CREATE_INVITE:
      return { ...state, step: 'add-members' };
    case ActionEnum.ADD_MEMBER:
      return { ...state, step: 'invite-members', members: [...state.members, action.payload] };
    case ActionEnum.REMOVE_MEMBER: {
      const members = state.members.filter((member) => member.id !== action.payload);
      return { ...state, members, step: members.length ? state.step : 'initial' };
    }
    case ActionEnum.CANCEL_ADD_MEMBER:
      return { ...state, step: state.members.length ? 'invite-members' : 'initial' };
    case ActionEnum.SET_INITIAL:
      return initialState;
  }
};

export const useInviteSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const addMember = (member: IMemberInvite) => dispatch({ type: ActionEnum.ADD_MEMBER, payload: member });
  const removeMember = (id: string) => dispatch({ type: ActionEnum.REMOVE_MEMBER, payload: id });
  const createInvite = () => dispatch({ type: ActionEnum.CREATE_INVITE, payload: {} });
  const cancelAddMember = () => dispatch({ type: ActionEnum.CANCEL_ADD_MEMBER, payload: {} });
  const setInitial = () => dispatch({ type: ActionEnum.SET_INITIAL, payload: {} });

  return { ...state, addMember, removeMember, createInvite, cancelAddMember, setInitial };
};

export default useInviteSlice;
